/**
 * AtlasMarketModule — the marketplace browser (Passport "Market" tab).
 *
 * Lists every active `market_listing` mirrored into shared state and lets the
 * player buy one at its fixed price. There is no bidding, no gacha, and no
 * random outcome: a buy is a single deterministic `buy_listing` reducer call
 * that the server settles atomically through the double-entry ledger (95% to
 * the seller, 5% marketplace tax). The UI only gates affordability + self-
 * purchase for UX; the server is the authority and rejects anything invalid.
 *
 * Event-driven: re-renders on `atlas:listings-updated` (the listing set
 * changed) and `atlas:wallet-updated` (affordability may have changed).
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { MARKET_TAX } from '../config.js';
import type { MarketListing } from '../state.js';

export class AtlasMarketModule implements IModule {
  readonly name = 'AtlasMarket';

  private list!: HTMLElement;
  private note!: HTMLElement;

  init(_app: App): void {
    this.list = document.getElementById('market-list') as HTMLElement;
    this.note = document.getElementById('market-note') as HTMLElement;

    bus.on('atlas:listings-updated', () => this.render());
    // A purchase/sale changes our SOV, which flips the affordability gating.
    bus.on('atlas:wallet-updated', () => this.render());

    this.render();
  }

  private render(): void {
    // Cheapest first — a simple, predictable ordering for a small market.
    const listings = [...atlas.listings.values()].sort((a, b) =>
      a.priceSov < b.priceSov ? -1 : a.priceSov > b.priceSov ? 1 : 0,
    );

    this.list.innerHTML = '';
    if (listings.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.textContent = 'No active listings.';
      this.list.appendChild(empty);
      return;
    }

    for (const listing of listings) {
      this.list.appendChild(this.renderListing(listing));
    }
  }

  private renderListing(listing: MarketListing): HTMLElement {
    const el = document.createElement('div');
    el.className = 'market-item';

    const asset = atlas.assets.get(listing.assetId.toString());
    const kind = asset ? `${asset.kind} #${listing.assetId}` : `asset #${listing.assetId}`;
    const isSelf = listing.seller === atlas.identityHex;
    const sellerLabel = isSelf ? 'you' : `${listing.seller.slice(0, 8)}\u2026`;

    // Display-only tax preview (server computes the authoritative bigint split).
    const tax = (listing.priceSov * BigInt(Math.round(MARKET_TAX * 10_000))) / 10_000n;
    const net = listing.priceSov - tax;
    const taxPct = (MARKET_TAX * 100).toFixed(0);

    const head = document.createElement('div');
    head.className = 'head';
    head.innerHTML =
      `<span class="kind">${kind}</span>` +
      `<span class="price">${listing.priceSov.toString()} SOV</span>`;
    el.appendChild(head);

    const meta = document.createElement('div');
    meta.className = 'meta';
    meta.textContent = `seller ${sellerLabel} · nets ~${net.toString()} after ${taxPct}% tax`;
    el.appendChild(meta);

    const actions = document.createElement('div');
    actions.className = 'actions';
    const buy = document.createElement('button');
    if (isSelf) {
      buy.textContent = 'Your listing';
      buy.disabled = true;
    } else if (atlas.walletSov < listing.priceSov) {
      buy.textContent = `Need ${listing.priceSov.toString()} SOV`;
      buy.disabled = true;
    } else {
      buy.textContent = `Buy · ${listing.priceSov.toString()} SOV`;
      buy.addEventListener('click', () => this.buy(listing.listingId));
    }
    actions.appendChild(buy);
    el.appendChild(actions);

    return el;
  }

  private buy(listingId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.setNote(`Buying listing #${listingId}\u2026`, '');
    void conn.reducers
      .buyListing({ listingId })
      .then(() => this.setNote(`Bought listing #${listingId}. Item is in your inventory.`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private setNote(text: string, cls: '' | 'ok' | 'err'): void {
    this.note.className = cls ? `note ${cls}` : 'note';
    this.note.textContent = text;
  }
}
